<script setup lang="ts">
import OzBaseButton, { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /**
     * Label corresponding to the button.
     */
    ariaLabel: string
    /**
     * Dark mode.
     */
    darkMode?: boolean | 'auto'
    /**
     * Direction.
     */
    dir?: 'ltr' | 'rtl' | 'auto'
    /**
     * Whether the button is disabled.
     */
    disabled?: boolean
    /**
     * See `OzBaseButton`.
     */
    href?: string
    /**
     * See `OzBaseButton`.
     */
    hrefMode?: OzBaseButtonHrefMode
    /**
     * Applies preset sizes/shapes.
     */
    sizePreset?: SizePreset
  }>(),
  {
    ariaLabel: '',
    darkMode: 'auto',
    dir: 'auto',
    disabled: false,
    href: undefined,
    hrefMode: OzBaseButtonHrefMode.Default,
    sizePreset: SizePreset.H40px,
  },
)

defineEmits<{
  (name: 'focus', e: FocusEvent): void
  (name: 'blur', e: FocusEvent): void
  (name: 'mouseenter', e: MouseEvent): void
  (name: 'mouseleave', e: MouseEvent): void
  (name: 'click', e: MouseEvent): void
}>()

const skewAngle = computed(() => {
  switch (props.sizePreset) {
    case SizePreset.H40px:
      return props.dir === 'rtl' ? -10 : 10
    default:
      return 0
  }
})
</script>

<script lang="ts">
enum SizePreset {
  H40px = 'H40px',
}

export { OzBaseButtonHrefMode as OzSkewedButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
export { SizePreset as OzSkewedButtonSizePreset }
</script>

<template>
  <OzBaseButton
    :aria-label="ariaLabel"
    :dir="dir"
    :disabled="disabled"
    :href="href"
    :href-mode="hrefMode"
    :class="[
      // SIZE & SHAPE
      // Common styles
      // Remove the default focus ring
      'focus-visible:outline-none',

      // Reset user agent styles
      // Don't set px-0 here so we won't have specificity issues
      'py-0',

      // Centralize button content
      'flex',
      'items-center',
      'justify-center',

      // Presets
      sizePreset === SizePreset.H40px && [
        'h-10',
        'px-3.5',
        'rounded-[5px]',
        'focus-visible:ring-[2.5px]',

        // Skew the button along the X axis
        {
          'skew-x-[10deg]': dir === 'ltr',
          'skew-x-[-10deg]': dir === 'rtl',
          'skew-x-[10deg] rtl:skew-x-[-10deg]': dir === 'auto',
        },
      ],

      // COLOR SCHEMES
      // Focus ring
      {
        'ring-grape-500': darkMode === false,
        'ring-canary-500': darkMode === true,
        'ring-grape-500 dark:ring-canary-500': darkMode === 'auto',
      },

      // Background
      {
        'active:!bg-grape-500': darkMode === false,
        'active:!bg-canary-500': darkMode === true,
        'active:!bg-grape-500 dark:active:!bg-canary-500': darkMode === 'auto',
      },

      // Text
      {
        'text-dark-text-200 active:text-light-text-200': darkMode === false,
        'text-light-text-200 active:text-dark-text-200': darkMode === true,
        'text-dark-text-200 dark:text-light-text-200 active:text-light-text-200 dark:active:text-dark-text-200':
          darkMode === 'auto',
      },
    ]"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
    @mouseenter="$emit('mouseenter', $event)"
    @mouseleave="$emit('mouseleave', $event)"
    @click="$emit('click', $event)"
  >
    <span
      :class="[
        'flex',
        'items-center',
        'justify-center',

        // Unskew the content on the X axis
        {
          'skew-x-[-10deg]': dir === 'ltr',
          'skew-x-[10deg]': dir === 'rtl',
          'skew-x-[-10deg] rtl:skew-x-[10deg]': dir === 'auto',
        },
      ]"
    >
      <slot></slot>
    </span>
  </OzBaseButton>
</template>
